import { useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import { Input, TabContent, TabPane } from 'reactstrap';
import { TabContext, TabPanel } from '@mui/lab';
import { Box, Stack } from '@mui/material';
import queryString from 'query-string';

import ActionBar from 'Lib/components/layout/ActionBar';
import MuiPageHeader from 'Lib/components/page/PageHeader';
import filterParameters from 'Lib/utilities/filterParameters';
import { screenSize } from 'Lib/utilities/screenSize';
import uppercase from 'Lib/utilities/uppercase';

import Pagination from '../../lists/Pagination';
import PageHeader from '../page/PageHeader';
import PageNav from '../page/PageNav';
import PageSidebar from '../page/PageSidebar';
import PageSidebarItem from '../page/PageSidebarItem';
import StickyWrapper from '../StickyWrapper';
import { SearchInput } from '../table/controls/Search';
import DesktopPagination from '../table/DesktopPagination';

import FiltersList from './FiltersList';
import PageBody from './PageBody';

import 'Lib/scss/_variables.scss';
import 'Lib/scss/_default.scss';

const Footer = ({ children, sticky, ...rest }) => {
	return sticky ? (
		<StickyWrapper {...rest} sticky bottom customFooter>
			{children}
		</StickyWrapper>
	) : (
		<div>{children}</div>
	);
};

const Page = props => {
	const location = useLocation();
	const history = useHistory();

	const {
		mui,
		apis,
		common,
		listTypeID,
		tabContent,
		breadcrumbs,
		hideBreadcrumbs = false,
		header = null,
		hideHeader = false,
		title,
		icon,
		backButton,
		sidebar,
		subTitle,
		subtitle,
		isLoading,
		footer,
		pagination = null,
		placeholder = null,
		search = false,
		filters,
		defaultFilters,
		filtersTopNav = true,
		hasList = false,
		showRefresh = false,
		fullScreen
	} = props;

	const [currentTab, setCurrentTab] = useState('');
	const [lazyLoad, setLazyLoad] = useState([]);
	const [hasLoaded, setHasLoaded] = useState([]);
	const [currentFilters, setCurrentFilters] = useState(defaultFilters ? filterParameters(defaultFilters).parsed : {});

	const { mobile } = screenSize();

	useEffect(() => {
		if (tabContent) {
			const newLazyLoad = tabContent.lazy?.map(item => (item.tab ? item.tab : item)) || [];
			setLazyLoad(newLazyLoad);
		}
	}, []);

	useEffect(() => {
		if (JSON.stringify(defaultFilters) !== JSON.stringify(currentFilters)) {
			setCurrentFilters(filterParameters(defaultFilters).parsed);
		}
	}, [defaultFilters]);

	const handleListFetchAndSetFilters = filters => {
		const previousQueryString = queryString.parse(history.location.search);
		const updatedFilterQueryString = { ...previousQueryString, ...filters };

		setCurrentFilters(updatedFilterQueryString);
		history.replace({ search: queryString.stringify(updatedFilterQueryString) });
		props.fetchList(updatedFilterQueryString);
	};

	const handlePageClick = (page, pageSize) => {
		const previousQueryString = queryString.parse(history.location.search);
		const updatedFilterQueryString = { ...previousQueryString };

		if (page) updatedFilterQueryString['page'] = page;
		if (pageSize) {
			updatedFilterQueryString['pageSize'] = pageSize;
			// added a condition for the old pagination (not mui) where page and pageSize values are always present whereas in the new (mui) pagination only the changed value is present
			if (previousQueryString['page'] == updatedFilterQueryString['page']) {
				updatedFilterQueryString['page'] = 1;
			}
		}

		setCurrentFilters(updatedFilterQueryString);
		history.push({ search: queryString.stringify(updatedFilterQueryString) });
		props.fetchList(updatedFilterQueryString);
	};

	const setTab = (tab, item) => {
		if (item?.url) {
			history.push(item.url);
			setCurrentTab(item.tabID);
		} else {
			const qs = queryString.parse(location.search);
			const tabID = item?.tabID ? item.tabID : tab;

			qs.page = item?.pagination?.page ?? 1;
			qs.tab = tabID.toLowerCase().replace(' ', '-');
			if (qs.tab !== 'maintenance') delete qs.workOrderType;
			history.push(`${location.pathname}?${queryString.stringify(qs)}`);

			setCurrentTab(tab);
		}

		if (lazyLoad.includes(tab) && !hasLoaded.includes(tab) && tabContent.lazy) {
			tabContent.lazy.map(item => {
				if (item.tab === tab && item.action) item.action();
			});

			const hasLoadedArray = [...hasLoaded];

			hasLoadedArray.push(tab);
			setHasLoaded(hasLoadedArray);
		}
	};

	const renderSidebar = config => {
		if (!config || config.objectID === undefined) return null;
		const { SidebarAlerts, SidebarFiles, SidebarLetters, SidebarNotes, SidebarWatchers } = props.components;

		const sidebarComponents = {
			notes: SidebarNotes,
			files: SidebarFiles,
			alerts: SidebarAlerts,
			letters: SidebarLetters,
			watchers: SidebarWatchers
		};

		return config.items.map((item, index) => {
			const Component = sidebarComponents[item];

			if (Component) {
				return (
					<PageSidebarItem key={index} title={uppercase(item)}>
						<Component {...config} />
					</PageSidebarItem>
				);
			} else if (typeof item === 'object') {
				return (
					<PageSidebarItem key={item.title} title={item.title}>
						{item.item()}
					</PageSidebarItem>
				);
			} else {
				throw new Error(
					'Please specify a known sidebar item in your sidebar prop, or create a new item with the shape of {title:"item title", item: () => <SideBarItem />}'
				);
			}
		});
	};

	const renderTabContent = Footer => {
		const { tab = tabContent.items[0].tabID } = queryString.parse(location.search);
		const activeTab = tabContent.items.find(item => item.tabID === tab);
		if (tabContent.items.filter(item => item.content).length === 0) return null;

		return (
			<TabContent activeTab={tab} className={activeTab && activeTab.contentClass}>
				{tabContent.items.map(({ isVisible = true, ...rest }, index) => {
					const item = { isVisible, ...rest };

					if (lazyLoad.includes(item.tabID) && tab !== item.tabID && !hasLoaded.includes(item.tabID)) return;

					return (
						isVisible && (
							<TabPane key={`${index}_${item.tabID}`} tabId={item.tabID ? item.tabID.toLowerCase().replace(' ', '-') : item.toLowerCase().replace(' ', '-')}>
								{item.content ? item.content(Footer) : null}
							</TabPane>
						)
					);
				})}
			</TabContent>
		);
	};

	const renderMuiTabContent = Footer => {
		const { tab = tabContent.items[0].tabID } = queryString.parse(location.search);

		return (
			<TabContext value={tab}>
				{tabContent.items.map(({ isVisible = true, ...rest }, index) => {
					const item = { isVisible, ...rest };

					if (lazyLoad.includes(item.tabID) && tab !== item.tabID && !hasLoaded.includes(item.tabID)) return;

					return (
						isVisible && (
							<TabPanel
								key={`${index}_${item.tabID}`}
								value={item.tabID ? item.tabID.toLowerCase().replace(' ', '-') : item.toLowerCase().replace(' ', '-')}
								sx={{ p: 0, display: 'contents' }}>
								{item.content ? item.content(Footer) : null}
							</TabPanel>
						)
					);
				})}
			</TabContext>
		);
	};

	const renderNav = () => {
		const { tab = tabContent.items[0].tabID } = queryString.parse(location.search);

		return (
			<PageNav
				current={tabContent.defaultTabID || tab}
				options={tabContent.items.map(({ isVisible = true, ...rest }) => {
					const item = { isVisible, ...rest };

					return {
						isVisible,
						label: item.title ? item.title : item.renderTab ? item.renderTab() : item.tabID ? uppercase(item.tabID) : uppercase(item),
						value: item.title || item.tabID ? item.tabID : item,
						onClick: () => setTab(item.tabID, item),
						url: item.url || ''
					};
				})}
				mui={mui}
				apis={apis}
				common={common}
				sidebar={sidebar}
				setTab={setTab}
				components={props.components}
				hasPermission={props.hasPermission}
			/>
		);
	};

	const renderActions = () => {
		const actions = typeof props.actions == 'function' ? props.actions(currentFilters) : props.actions;
		const actionsMenu = [];

		if (!actions || actions.length < 1) return;

		if (actions.render) return actions;

		actions.map(action => {
			if (action.permissions) {
				const criteria = action.permissions.map(permission => props.hasPermission?.(permission.component, permission.access));

				if (!criteria.includes(false)) {
					const editAction = action;
					delete editAction.permissions;
					actionsMenu.push(editAction);

					return action;
				}
			} else {
				actionsMenu.push(action);

				return action;
			}

			return action;
		});

		return actionsMenu;
	};

	const Header = headerProps => <StickyWrapper>{headerProps.children}</StickyWrapper>;

	// need to still make header not sticky when responsive
	return (
		<main className="page-container" style={{ boxShadow: fullScreen ? 'none' : '0px 1px 2px #B6C7D6' }}>
			<Box
				id="layout"
				className="page-layout"
				sx={
					(pagination && pagination.totalPages > 1) || footer
						? {
								display: 'grid',
								gridTemplateRows: 'auto 1fr auto'
							}
						: { display: 'flex', flexDirection: 'column' }
				}>
				{/* HEADER */}
				{!hideHeader && (
					<StickyWrapper backgroundColor={header?.color || false}>
						{mui ? (
							<MuiPageHeader icon={icon} backButton={backButton} subtitle={subtitle} breadcrumbs={breadcrumbs} title={title} actions={renderActions()} />
						) : (
							<PageHeader
								title={typeof title == 'function' ? title() : title}
								subTitle={typeof subTitle == 'function' ? subTitle() : subTitle}
								breadcrumbs={breadcrumbs}
								hideBreadcrumbs={hideBreadcrumbs}
								actions={renderActions()}
								renderActions={props.renderActions}
							/>
						)}

						{header && header.render && header.render()}

						{/* for when the tabContent should be above the nav */}
						{((filters && !tabContent) || (filters && filtersTopNav)) && !mui && (
							<Box p={tabContent ? '0 25px' : '5px 25px'} display="flex" alignItems="flex-start" justifyContent="space-between" width="100%">
								<Box alignSelf="flex-end" width="100%">
									<FiltersList
										mui={mui}
										apis={apis}
										listTypeID={listTypeID}
										currentTab={currentTab}
										showRefresh={showRefresh}
										config={props.config}
										filters={filters}
										defaultFilters={defaultFilters}
										handler={handleListFetchAndSetFilters}
									/>
								</Box>
							</Box>
						)}

						{tabContent && (
							<Box mb={mobile && !props.files ? 2 : 0}>
								{tabContent.items?.length > 0 && renderNav()}
								{search && mui ? (
									<Box m={'25px 16px 0 16px'}>
										<SearchInput placeholder={search.placeholder} onChange={search.handler} />
									</Box>
								) : (
									search && (
										<Box sx={{ float: 'right', width: '50%', padding: '10px' }}>
											<Input
												autoFocus
												data-cy="search-input"
												id="search"
												name="search"
												type="search"
												onChange={search.handler}
												placeholder={search.placeholder}
											/>
										</Box>
									)
								)}

								{filters && !filtersTopNav && !mui && (
									<Box p="1vh 1vw">
										<FiltersList
											apis={apis}
											listTypeID={listTypeID}
											currentTab={currentTab}
											showRefresh={showRefresh}
											config={props.config}
											filters={filters}
											defaultFilters={defaultFilters}
											handler={handleListFetchAndSetFilters}
										/>
									</Box>
								)}
							</Box>
						)}

						{!tabContent && search && (
							<Box sx={{ float: 'right', width: '50%', padding: '10px', marginBottom: '9px' }}>
								<Input autoFocus data-cy="search-input" id="search" name="search" type="search" onChange={search.handler} placeholder={search.placeholder} />
							</Box>
						)}
					</StickyWrapper>
				)}

				{!hasList && <Box sx={{ pt: '1em' }} />}

				{/* BODY */}
				<PageBody {...props}>
					{props.children?.({
						footer: Footer,
						props: { ...props },
						header: Header
					})}
					{tabContent?.items.length > 0 ? (mui ? renderMuiTabContent(Footer) : renderTabContent(Footer)) : null}
				</PageBody>

				{/* FOOTER */}
				{props.footer || (pagination && !isLoading) || (props.actions && !isLoading) || props.files ? (
					<div aria-label="pagination and actions">
						{mui ? (
							<>
								<Stack>
									{pagination && !mobile && <DesktopPagination pagination={pagination} onPageChange={handlePageClick} actions={common?.actions} />}
									<ActionBar
										handlePageClick={handlePageClick}
										pagination={pagination}
										files={props.files}
										actions={renderActions()}
										apis={apis}
										common={common}
										components={props.components}
										fetch={props.fetchList}
										filters={filters}
										listTypeID={listTypeID}
										placeholder={placeholder}
										sidebar={sidebar}
										hasPermission={props.hasPermission}
									/>
								</Stack>
								{props.footer ? props.footer?.render(Footer) : null}
								<Box id="footer-portal" />
							</>
						) : (
							<StickyWrapper bottom>
								{footer && footer.render()}
								<Box>{pagination && !isLoading && <Pagination fetchList={props.fetchList} pagination={pagination} onClick={handlePageClick} />}</Box>
							</StickyWrapper>
						)}
					</div>
				) : null}
			</Box>

			{sidebar && !mui && (
				<Box sx={{ zIndex: '1000 !important' }}>
					<PageSidebar>{renderSidebar(sidebar)}</PageSidebar>
				</Box>
			)}
		</main>
	);
};

export default withRouter(Page);
